import React, { useContext, useEffect, useState } from 'react';

import { spawnIncomingCallNotification } from 'flow/actions/app-actions';
import { lookupNumber } from 'flow/actions/contact-actions';
import {
    conferenceToMeeting,
    fetchAllMeetingNumbers,
} from 'flow/actions/meeting-actions';
import { getPinCode } from 'flow/actions/phone-actions';
import { fetchCallWidgets } from 'flow/actions/run-url-actions';
import FocusedLocalCallContext from 'flow/contexts/call-item-size-context';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    getAgentInfo,
    getDisplayMode,
    getOwnActiveCallByCallId,
    hasPendingAttendedTransfer,
} from 'flow/selectors/call-selectors';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { getConferenceNumber } from 'flow/selectors/meeting-selectors';
import { selectCentionDeeplinkForCallWithCallerNumber } from 'flow/selectors/omnichannel-selectors';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import CallOnOtherDevice from './CallOnOtherDevice';
import InstallDesktopAppBanner from 'flow/companion/InstallDesktopAppBanner';
import CoListeningSupervisorWrapper from './CoListeningSupervisorWrapper';
import LargeCallItem from './LargeCallItem';
import PendingAttendedTransferCallItems from './PendingAttendedTransferCallItems';
import SmallCallItem from './SmallCallItem';

// Not every SIP server sends us a locutus session id for queue calls, so we can't rely on it
// alone to dedupe notifications (that would mean queue calls never notify at all). Prefer it
// when present, since it dedupes across terminals/devices, but fall back to the call's own
// (always-available) id so the notification still fires when it never arrives.
//
// locutusSessionId can arrive *after* a call has already notified via the callId fallback
// (receiveQueueCallInfo, which never sets it, can land before receiveNumberInfo, which does).
// Guarding — and recording — callId unconditionally, in addition to locutusSessionId when it's
// known, stops that from notifying twice for the same call.
export const hasAlreadyNotifiedForCall = (
    callId: string,
    locutusSessionId: string | undefined,
    spawnedNotifications: string[],
): boolean =>
    spawnedNotifications.includes(callId) ||
    (!!locutusSessionId && spawnedNotifications.includes(locutusSessionId));

export const notificationDedupKeysFor = (
    callId: string,
    locutusSessionId: string | undefined,
): string[] => (locutusSessionId ? [callId, locutusSessionId] : [callId]);

interface Props {
    callId: string;
    direction: string;
    remoteNumber: string;
    localNumber?: string;
    ownCountryCode: string;
    startTime?: Date;
    isCoListening?: boolean;
    isOnHold?: boolean;
    isAccepted: boolean;
    isEnded: boolean;
    failCause?: string;
    isQueueCall?: boolean;
    hasNumberFromTop?: boolean;
    onAnswer?: () => void;
    onHangup?: () => void;
    onHold?: () => void;
    onUnhold?: () => void;
    onSwap?: () => void;
    onMerge?: () => void;
    onTransferStart?: () => void;
    spawnedNotifications: string[];
    onNotificationSpawned: (notificationDedupKey: string) => void;
    isRemote?: boolean;
    /**
     * Shown in the banner instead of "call on another device". Companion mode uses it to say the
     * call is running through the desktop app, which is the only sign the user gets that their
     * audio is on a different machine.
     */
    remoteLabel?: string;
}

const CallItem = ({
    callId,
    remoteLabel,
    onSwap,
    onHold,
    isEnded,
    onMerge,
    isOnHold,
    onUnhold,
    onAnswer,
    onHangup,
    direction,
    startTime,
    failCause,
    isAccepted,
    localNumber,
    isQueueCall,
    remoteNumber,
    isCoListening,
    ownCountryCode,
    onTransferStart,
    hasNumberFromTop,
    spawnedNotifications,
    onNotificationSpawned,
    isRemote,
}: Props) => {
    const dispatch = useAppDispatch();

    const ownActiveCall = useAppSelector((state) =>
        getOwnActiveCallByCallId(state, callId),
    );
    const queueDescriptionFromActiveCall = useAppSelector((state) =>
        state.pbx.getIn(['queues', ownActiveCall?.get('queue'), 'description']),
    );

    const [queueDescription, setQueueDescription] = useState(
        queueDescriptionFromActiveCall,
    );

    const [hasReceivedLookupNumber, setHasReceivedLookupNumber] =
        useState(false);

    const isConference = ownActiveCall?.has('conference');
    const conferenceNumber = useAppSelector((state) =>
        getConferenceNumber(state, ownActiveCall, ownCountryCode),
    );
    const conferenceMeetingCode = ownActiveCall?.getIn(
        ['conference', 'meetingCode'],
        null,
    );
    const conferencePinCode = ownActiveCall?.getIn(
        ['conference', 'pinCode'],
        null,
    );
    const fetchPinCodeAsync = async (meetingCode: string) => {
        return await dispatch(
            getPinCode(ownActiveCall?.get('key'), meetingCode),
        );
    };

    const contact: TypedMap<Contact> = useAppSelector((state) => {
        const getContact = makeGetContactDataForNumber();
        //@ts-ignore
        return getContact(state, remoteNumber);
    });

    const agentInfo = useAppSelector((state) => getAgentInfo(state, callId));

    const centionDeeplink = useAppSelector((state) =>
        selectCentionDeeplinkForCallWithCallerNumber(state, remoteNumber),
    );

    useEffect(() => {
        dispatch(fetchAllMeetingNumbers());
    }, [fetchAllMeetingNumbers]);

    useEffect(() => {
        const fetchData = () => {
            setHasReceivedLookupNumber(false);
            if (remoteNumber && !contact) {
                addUserPhoneLogEntry(`Doing number lookup on ${remoteNumber}`);
                try {
                    dispatch(lookupNumber(remoteNumber));
                    setHasReceivedLookupNumber(true);
                } catch (error) {
                    addUserPhoneLogEntry(
                        'Error caching remote number as lookup number',
                        error,
                    );
                }
            } else if (remoteNumber && contact) {
                const sourceDescription =
                    contact.get('sourceDescription') ||
                    contact.get('contactSource');
                setHasReceivedLookupNumber(true);
                const source = sourceDescription
                    ? `and is sourced from: ${sourceDescription}`
                    : '';
                addUserPhoneLogEntry(
                    `Contact with phone number: ${remoteNumber} has contact type: ${contact.get(
                        'type',
                    )} ${source}`,
                );
            }
        };

        fetchData();
    }, [remoteNumber, contact, lookupNumber]);

    useEffect(() => {
        //For queue calls we do not want to show the notification until we have gotten a number from top
        //because otherwise calls from queues with a suffix will sometimes display with suffix and sometimes without.
        const hasNumberFromTopIfQueueCall = !isQueueCall || hasNumberFromTop;
        const locutusSessionId = ownActiveCall?.get('locutusSessionId');
        if (
            !!ownCountryCode &&
            !!remoteNumber &&
            direction === 'incoming' &&
            !isAccepted &&
            !!ownActiveCall &&
            !hasAlreadyNotifiedForCall(
                callId,
                locutusSessionId,
                spawnedNotifications,
            ) &&
            hasNumberFromTopIfQueueCall &&
            hasReceivedLookupNumber
        ) {
            dispatch(
                spawnIncomingCallNotification(
                    remoteNumber,
                    ownCountryCode,
                    queueDescription,
                ),
            );
            notificationDedupKeysFor(callId, locutusSessionId).forEach(
                onNotificationSpawned,
            );
            fetchCallWidgets();
        }
    }, [
        spawnIncomingCallNotification,
        spawnedNotifications,
        onNotificationSpawned,
        remoteNumber,
        ownActiveCall,
        callId,
        direction,
        isAccepted,
        ownCountryCode,
        fetchCallWidgets,
        queueDescription,
        isQueueCall,
        hasNumberFromTop,
        hasReceivedLookupNumber,
    ]);

    // We store the queue description in the state only when it is not undefined so that we do
    // not spawn a notification if it goes from defined to undefined, which happens when the active
    // call is removed from the store.
    useEffect(() => {
        if (queueDescriptionFromActiveCall) {
            setQueueDescription(queueDescriptionFromActiveCall);
        }
    }, [queueDescriptionFromActiveCall]);

    const focusedLocalCallId = useContext(FocusedLocalCallContext);
    const displayMode = useAppSelector(getDisplayMode);

    const itemSize = (() => {
        if (DisplayMode.MINIMIZED.is(displayMode)) {
            return 'small';
        }

        // If there is no focused local call id, it is a remote call, which are always focused.
        if (!focusedLocalCallId) {
            return 'large';
        }

        return focusedLocalCallId === callId ? 'large' : 'small';
    })();

    let conferenceToMeetingCall,
        decline,
        hangup,
        swap = onSwap,
        answer,
        merge,
        hold = onHold,
        unHold = onUnhold,
        startingTime;

    if (failCause || isEnded) {
        swap = null;
        hold = null;
        unHold = null;
        conferenceToMeetingCall = null;
    } else if (!failCause && isAccepted) {
        startingTime = startTime;
        hangup = onHangup;
        merge = onMerge;
    } else if (direction === 'incoming' && !isAccepted) {
        decline = onHangup;
        answer = onAnswer;
    } else if (direction === 'outgoing' && !isAccepted) {
        hangup = onHangup;
    }

    const isPendingTransfer = useAppSelector(hasPendingAttendedTransfer);

    if (isPendingTransfer) {
        return (
            <PendingAttendedTransferCallItems
                itemSize={itemSize}
                displayMode={displayMode}
                direction={direction}
                contact={contact}
                remoteNumber={remoteNumber}
                ownCountryCode={ownCountryCode}
            />
        );
    }
    if (isCoListening) {
        return (
            <CoListeningSupervisorWrapper
                subjectContact={contact}
                subjectNumber={remoteNumber}
                startTime={startingTime}
                onHangup={onHangup}
                itemSize={itemSize}
            />
        );
    }
    if (isConference && !(failCause || isEnded)) {
        conferenceToMeetingCall = () => dispatch(conferenceToMeeting());
    }

    return itemSize === 'large' ? (
        <>
            {isRemote && (
                <CallOnOtherDevice
                    className="rounded-b-none p-3"
                    label={remoteLabel}
                />
            )}
            <InstallDesktopAppBanner />
            <LargeCallItem
                activeCall={ownActiveCall}
                agentInfo={agentInfo}
                callId={callId}
                centionDeeplink={centionDeeplink}
                conferenceMeetingCode={conferenceMeetingCode}
                conferenceToMeeting={conferenceToMeetingCall}
                conferenceNumber={conferenceNumber}
                conferencePinCode={conferencePinCode}
                contact={contact}
                direction={direction}
                failCause={failCause}
                getPinCode={fetchPinCodeAsync}
                isAccepted={isAccepted}
                isConference={isConference}
                isEnded={isEnded}
                isOnHold={isOnHold}
                localNumber={localNumber}
                onAnswer={answer}
                onDecline={decline}
                onHangup={hangup}
                onHold={hold}
                onMerge={merge}
                onSwap={swap}
                onTransferStart={onTransferStart}
                onUnhold={unHold}
                ownCountryCode={ownCountryCode}
                remoteNumber={remoteNumber}
                startTime={startingTime}
            />
        </>
    ) : (
        <SmallCallItem
            activeCall={ownActiveCall}
            agentInfo={agentInfo}
            callId={callId}
            centionDeeplink={centionDeeplink}
            conferenceMeetingCode={conferenceMeetingCode}
            conferenceToMeeting={conferenceToMeetingCall}
            conferenceNumber={conferenceNumber}
            conferencePinCode={conferencePinCode}
            contact={contact}
            direction={direction}
            failCause={failCause}
            getPinCode={fetchPinCodeAsync}
            isAccepted={isAccepted}
            isConference={isConference}
            isEnded={isEnded}
            isOnHold={isOnHold}
            onAnswer={answer}
            onDecline={decline}
            onHangup={hangup}
            onHold={hold}
            onMerge={merge}
            onSwap={swap}
            onTransferStart={onTransferStart}
            onUnhold={unHold}
            ownCountryCode={ownCountryCode}
            remoteNumber={remoteNumber}
            startTime={startingTime}
            isRemote={isRemote}
        />
    );
};

export default CallItem;
